import { supabase } from './supabase'
import { haversineKm } from './routeOptimizer'
import { matchesDivision, type DivisionSelection } from './divisionFiltering'

export interface QcRotationContract {
  id: string
  contract_number?: string | null
  title?: string | null
  last_qc_at?: string | null
  qc_rush_requested_at?: string | null
  property_id?: string | null
  operational_division_id?: string | null
  service_type?: string | null
  customer?: { company_name?: string | null } | null
  property?: {
    zip_code?: string | null
    latitude?: number | null
    longitude?: number | null
    address?: string | null
    name?: string | null
  } | null
}

/**
 * Order contracts for the QC rotation queue:
 *   1. Rush-requested contracts first (oldest rush request first).
 *   2. Contracts never QC'd (last_qc_at is null).
 *   3. Everyone else, oldest last_qc_at first.
 */
export function sortQcRotationQueue<T extends QcRotationContract>(contracts: T[]): T[] {
  return [...contracts].sort((a, b) => {
    const aRush = !!a.qc_rush_requested_at
    const bRush = !!b.qc_rush_requested_at
    if (aRush !== bRush) return aRush ? -1 : 1
    if (aRush && bRush) {
      return (
        new Date(a.qc_rush_requested_at as string).getTime() -
        new Date(b.qc_rush_requested_at as string).getTime()
      )
    }

    const aNull = a.last_qc_at == null
    const bNull = b.last_qc_at == null
    if (aNull !== bNull) return aNull ? -1 : 1
    if (aNull && bNull) return 0

    return new Date(a.last_qc_at as string).getTime() - new Date(b.last_qc_at as string).getTime()
  })
}

function zipPrefix(zip?: string | null): string {
  return String(zip || '')
    .replace(/\D/g, '')
    .slice(0, 3)
}

function distanceKm(a: QcRotationContract, b: QcRotationContract): number {
  const al = a.property?.latitude
  const ao = a.property?.longitude
  const bl = b.property?.latitude
  const bo = b.property?.longitude
  if (al == null || ao == null || bl == null || bo == null) return Number.POSITIVE_INFINITY
  return haversineKm(al, ao, bl, bo)
}

/**
 * Build a day's QC batch from the rotation queue.
 * Seed = front of queue (or first rush). Then fill with nearby contracts that are
 * still near the front (within rankWindow positions), preferring same zip prefix
 * then haversine distance. Never pulls someone from far back of the line.
 */
export function clusterQcDayBatch(
  queue: QcRotationContract[],
  capacity = 8,
  rankWindow = 15
): QcRotationContract[] {
  if (queue.length === 0 || capacity <= 0) return []
  const seed = queue[0]
  const batch: QcRotationContract[] = [seed]
  const used = new Set([seed.id])

  const candidates = queue.slice(1, Math.max(2, rankWindow + 1)).filter((c) => !used.has(c.id))

  while (batch.length < capacity && candidates.length > 0) {
    const anchor = batch[batch.length - 1]
    const seedZip = zipPrefix(seed.property?.zip_code)

    candidates.sort((a, b) => {
      const aSameZip = zipPrefix(a.property?.zip_code) === seedZip && seedZip !== ''
      const bSameZip = zipPrefix(b.property?.zip_code) === seedZip && seedZip !== ''
      if (aSameZip !== bSameZip) return aSameZip ? -1 : 1
      return distanceKm(anchor, a) - distanceKm(anchor, b)
    })

    const next = candidates.shift()!
    batch.push(next)
    used.add(next.id)
  }

  return batch
}

/** Fetches active/in-flight contracts ordered by the rotation queue rules above. */
export async function fetchQcRotationQueue(
  limit = 100,
  division?: DivisionSelection | null
): Promise<QcRotationContract[]> {
  const fullSelect = `id, contract_number, title, last_qc_at, qc_rush_requested_at, property_id, operational_division_id, service_type,
       customer:customers(company_name),
       property:properties(zip_code, latitude, longitude, address, name)`
  const fallbackSelect = `id, contract_number, title, last_qc_at, qc_rush_requested_at, property_id, operational_division_id, service_type,
       customer:customers(company_name),
       property:properties(zip_code, address, name)`
  const queryLimit = division ? Math.max(limit, 1000) : limit

  let data: any = null
  let error: any = null

  const first = await supabase
    .from('contracts')
    .select(fullSelect)
    .in('status', ['active', 'in_process', 'signed'])
    .or('is_recurring.eq.true,contract_type.eq.recurring,contract_type.eq.as_needed')
    .limit(queryLimit)
  data = first.data
  error = first.error

  if (error && /latitude|longitude/i.test(error.message || '')) {
    const second = await supabase
      .from('contracts')
      .select(fallbackSelect)
      .in('status', ['active', 'in_process', 'signed'])
      .or('is_recurring.eq.true,contract_type.eq.recurring,contract_type.eq.as_needed')
      .limit(queryLimit)
    data = second.data
    error = second.error
  }

  // last_qc_at / qc_rush may be missing if rotation migration not applied
  if (error && /last_qc_at|qc_rush_requested_at/i.test(error.message || '')) {
    const third = await supabase
      .from('contracts')
      .select(
        `id, contract_number, title, property_id, operational_division_id, service_type,
         customer:customers(company_name),
         property:properties(zip_code, address, name)`
      )
      .in('status', ['active', 'in_process', 'signed'])
      .or('is_recurring.eq.true,contract_type.eq.recurring,contract_type.eq.as_needed')
      .limit(queryLimit)
    data = third.data
    error = third.error
  }

  if (error) throw error

  const rows = (data || [])
    .map((row: any) => ({
      ...row,
      customer: Array.isArray(row.customer) ? row.customer[0] || null : row.customer,
      property: Array.isArray(row.property) ? row.property[0] || null : row.property,
    }))
    .filter((row: QcRotationContract) => matchesDivision(row, division)) as QcRotationContract[]

  return sortQcRotationQueue(rows).slice(0, limit)
}

/** Marks a contract as rush-requested (moves it to the front of the queue). */
export async function requestQcRush(contractId: string): Promise<void> {
  const { error } = await supabase
    .from('contracts')
    .update({ qc_rush_requested_at: new Date().toISOString() })
    .eq('id', contractId)
  if (error) throw error
}

/** Clears the rush flag and stamps last_qc_at — call once a QC visit is completed. */
export async function markContractQcCompleted(contractId: string): Promise<void> {
  const { error } = await supabase
    .from('contracts')
    .update({ last_qc_at: new Date().toISOString(), qc_rush_requested_at: null })
    .eq('id', contractId)
  if (error) throw error
}
